import React from 'react';
import { Link } from 'react-router-dom';
import { Landmark, Handshake, CheckCircle2, ArrowRight, Users, ShieldCheck, TrendingUp } from 'lucide-react';
import { Seo, Breadcrumbs, breadcrumbLd, MarketingShell, CtaBlock, PageHero, Band } from '@/seo/kit';

export default function PartnersPage() {
  const crumbs = [
    { label: 'Home', href: '/' },
    { label: 'Partners', href: '/partners' },
  ];

  const lenderBenefits = [
    'Every borrower draw arrives complete and verified against your requirements',
    'Your team spends less time chasing paperwork',
    'Lien waivers, budgets, and receipts organized the same way every time',
    'A cleaner, consistent audit trail on every project',
    'Optional co-branding so your borrowers see your name on a tool that works',
  ];

  const affiliateBenefits = [
    'Refer the builders and developers you already work with',
    'They get their draws bank-ready; you get credit for every referral',
    'A simple referral code tied to your account',
  ];

  return (
    <MarketingShell>
      <Seo
        title="Partner With DrawReady — For Lenders & Referral Partners"
        description="A missing document shouldn't stall a draw — or a funding. Partner with DrawReady so every borrower's draw arrives bank-ready. Programs for lenders and referral partners."
        path="/partners"
        jsonLd={breadcrumbLd(crumbs)}
      />
      <PageHero top={<Breadcrumbs items={crumbs} />} eyebrow="Partner Program" title="Partner with DrawReady" bg="#EEF2F8">
        A single missing document stalls a draw — and a stalled draw stalls a project and a funding. When the builders you work with use DrawReady, their draws arrive complete, organized, and verified before they ever reach a lender. That means complete packages and less paperwork for everyone.
      </PageHero>

      <Band bg="#fff">
        {/* Two tracks */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Lenders */}
          <div className="rounded-lg border p-7" style={{ borderColor: '#D8DEE8' }} data-testid="partner-track-lenders">
            <Landmark className="w-8 h-8" style={{ color: '#1E4ED8' }} />
            <h2 className="text-2xl mt-3">For Lenders &amp; Loan Officers</h2>
            <p className="mt-1" style={{ color: '#5B6678' }}>Your borrowers, better prepared — so your team spends less time chasing paperwork and more time funding.</p>
            <ul className="space-y-2.5 mt-5">
              {lenderBenefits.map((b) => (
                <li key={b} className="flex items-start gap-3" style={{ color: '#1D2939' }}><span className="mt-2.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: '#13294B' }} /> {b}</li>
              ))}
            </ul>
            <Link to="/contact" data-testid="partner-lender-cta" className="btn-primary mt-6">Request a lender demo</Link>
          </div>

          {/* Affiliates */}
          <div className="rounded-lg border p-7" style={{ borderColor: '#D8DEE8' }} data-testid="partner-track-affiliates">
            <Users className="w-8 h-8" style={{ color: '#1E4ED8' }} />
            <h2 className="text-2xl mt-3">For Referral Partners</h2>
            <p className="mt-1" style={{ color: '#5B6678' }}>Consultants, CPAs, associations, and anyone who works with builders — refer the people you already help.</p>
            <ul className="space-y-2.5 mt-5">
              {affiliateBenefits.map((b) => (
                <li key={b} className="flex items-start gap-3" style={{ color: '#1D2939' }}><span className="mt-2.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: '#13294B' }} /> {b}</li>
              ))}
            </ul>
            <Link to="/contact" data-testid="partner-affiliate-cta" className="btn-secondary mt-6">Let's schedule a call</Link>
          </div>
        </div>
      </Band>

      <Band bg="#F2F4F7">
        <h2 className="text-2xl md:text-3xl mb-6" style={{ color: '#13294B', fontWeight: 700 }}>Why it works</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="rounded-lg border p-5 bg-white" style={{ borderColor: '#D8DEE8' }}><ShieldCheck className="w-6 h-6" style={{ color: '#1E4ED8' }} /><div className="font-semibold mt-2" style={{ color: '#13294B' }}>Verified before it's sent</div><div className="text-sm mt-1" style={{ color: '#5B6678' }}>Draws are checked against lender requirements up front.</div></div>
          <div className="rounded-lg border p-5 bg-white" style={{ borderColor: '#D8DEE8' }}><TrendingUp className="w-6 h-6" style={{ color: '#1E4ED8' }} /><div className="font-semibold mt-2" style={{ color: '#13294B' }}>Complete every time</div><div className="text-sm mt-1" style={{ color: '#5B6678' }}>Every draw is organized the same way against your requirements.</div></div>
          <div className="rounded-lg border p-5 bg-white" style={{ borderColor: '#D8DEE8' }}><Handshake className="w-6 h-6" style={{ color: '#1E4ED8' }} /><div className="font-semibold mt-2" style={{ color: '#13294B' }}>Built to grow with you</div><div className="text-sm mt-1" style={{ color: '#5B6678' }}>Co-branding and referral options as the relationship grows.</div></div>
        </div>
      </Band>

      <Band bg="#fff"><CtaBlock /></Band>
    </MarketingShell>
  );
}
